import { unique } from '../../../utilities';
import { makeVisualConfigValid, checkDimension, testVisualConfiugration } from '../../../content/creator';
import { IRequestContext } from '../../../domain/shared/core/IRequestContext';
import { Result } from '../../../domain/shared/core/Result';
import { User } from '../../../domain/user/entitites/user';
import { Content, IContentProps } from '../entities/content';
import { DataRows } from '../entities/valueobjects/DataRows';
import { DimensionConfig, Dimensions, UniqueFilter, VisualConfiguration } from '../entities/valueobjects/Dimensions';
import { DataSetFields } from '../entities/valueobjects/Fields';
import { Share } from '../entities/valueobjects/Shares';

// biome-ignore lint/complexity/noStaticOnlyClass: erich
export class ContentService {
  /**
   * Decides wether a content is writeable by the given user
   * TReimplementation of canWriteContent when
   * refactored delete canWriteContent and use this
   * @param content
   * @param user
   * @param context
   * @returns Result<boolean>
   */
  public static canWrite(content: Content<IContentProps>, user: User, context: IRequestContext): Result<boolean> {
    if (!content) return Result.fail({ statusCode: 404, message: 'Content missing', context: context });
    if (!user) return Result.fail({ statusCode: 404, message: 'User: missing', context: context });
    if (user.slug === 'anonymous-1') return Result.fail({ statusCode: 403, message: `User: ${user.slug} is not allowed to write`, context: context });
    //owner
    if (content.userId === user.getId().toString()) {
      return Result.ok(true);
    }
    if (!content.shares || content.shares.length === 0) {
      return Result.fail({ statusCode: 403, message: `Content: ${content.slug} is not shared with you ${user.slug}`, context: context });
    }
    //do we have at least one entry in shares that i can write there
    for (const share of content.shares) {
      if (share.slug === user.slug && share.canWrite) {
        return Result.ok(true);
      }
    }
    //Default return false
    return Result.fail({ statusCode: 403, message: `Content: ${content.slug} is not shared with you${user.slug}`, context: context });
  }
  /**
   * Decides wether a user can read a given Content Entity
   * Reimplementation of canReadContent when
   * refactored delete canReadContent and use this
   * @param content
   * @param user
   */
  public static canRead(content: Content<IContentProps>, user: User, context: IRequestContext): Result<boolean> {
    if (!content) return Result.fail({ statusCode: 404, message: 'Content missing', context: context });
    if (!user) return Result.fail({ statusCode: 404, message: 'User: missing', context: context });
    if (content.isPublic) {
      return Result.ok(true);
    }
    if (content.userId === user.getId()) {
      return Result.ok(true);
    }
    if (Array.isArray(content.shares)) {
      for (const share of content.shares) {
        if (share.slug === user.slug) {
          return Result.ok(true);
        }
      }
    }

    return Result.fail({ statusCode: 403, message: `Content: ${content.slug} is not shared with you`, context: context });
  }

  /**
   * Creates a visual Configuration given
   * available fields, dataRows and the type
   * of vis you want created.
   * @param type
   * @param fields
   * @param data
   * @param context
   * @returns
   */
  public static createVisualConfiguration(
    type: string,
    fields: DataSetFields,
    data: DataRows,
    context: IRequestContext,
    uniqueFilters: UniqueFilter[] = [],
    dimensions: DimensionConfig[] = [],
  ): Result<VisualConfiguration> {
    const dimensionRequirements = checkDimension(fields, type);
    if (!dimensionRequirements.fulfilled) {
      return Result.fail({ statusCode: 500, context: context, message: `dataset does not fulfill dimension requirements for vizType: ${type}` });
    }

    let visualConfiguration: VisualConfiguration = {
      type: type,
      valid: false,
      dimensions: dimensionRequirements.dimensions,
      uniqueFilters: [],
    };

    //overwrite manual options
    if (uniqueFilters && uniqueFilters.length > 0) {
      visualConfiguration.uniqueFilters = uniqueFilters;
    }

    if (dimensions && dimensions.length > 0) {
      for (const dimension of dimensions) {
        const curDimension = dimensions.find((el) => el.dimName === dimension.dimName);
        if (curDimension) {
          curDimension.fieldName = dimension.fieldName;
        }
      }
    }

    const valid = testVisualConfiugration(visualConfiguration, data, fields);
    visualConfiguration.valid = valid;

    if (!valid) {
      visualConfiguration = makeVisualConfigValid(visualConfiguration, fields, data);
    }

    if (!visualConfiguration.valid) {
      return Result.fail({ statusCode: 500, context: context, message: 'cant find dimension / unique filter combination that generates a valid viz' });
    }

    return Result.ok(visualConfiguration);
  }

  public static getShares(
    user: User,
    baseShares: Array<Share>,
    baseOwnerSlug: string,
    parentShares: Array<Share>,
    parentOwnerSlug: string,
    context: IRequestContext,
  ) {
    let shares: Array<Share> = [];
    //do we have direct shares?
    //wenn ein item gemoved wird und der share nicht direkt gesetzt ist,
    //werden shares vom alten parent nicht übernommen.
    // direkt shares of the item stay in!
    //use folder Permissions root = false
    if (baseShares) {
      for (const share of baseShares) {
        if (share.isRoot) {
          shares.push(share);
        }
      }
    }

    // use folder Permissions root = false
    if (parentShares) {
      shares = shares.concat(
        parentShares.map((el) => {
          el.isRoot = false;
          return el;
        }),
      );
    }

    // if owner of content !== parent owner add share for the owner
    if (parentOwnerSlug !== user.slug) {
      shares.push({
        slug: parentOwnerSlug,
        type: 'user',
        canWrite: true,
        canShare: true,
        isRoot: false,
        creator: user.slug,
        updatedAt: new Date(),
        createdAt: new Date(),
      });
    }

    // avoid shares with myself
    if (baseOwnerSlug === user.slug) {
      //content is owned by the user we dont need a share
      const finalShares: Share[] = [];
      for (const share of shares) {
        if (share.slug !== user.slug) {
          finalShares.push(share);
        }
      }

      shares = finalShares;
    }

    return shares;
  }

  public static getShareCoding(shares: Array<Share>) {
    let shareStrings: string[] = [];
    for (const share of shares) {
      shareStrings = shareStrings.concat(ContentService.getShareStrings(share));
    }

    return unique(shareStrings);
  }

  private static getShareStrings = (share) => {
    const shareStrings: string[] = [];
    if (!share || !share.slug) return shareStrings;

    shareStrings.push(`${share.slug}r${share.isRoot ? 'y' : 'n'}`);
    if (share.canWrite || share.canShare) shareStrings.push(`${share.slug}w${share.isRoot ? 'y' : 'n'}`);
    if (share.canShare) shareStrings.push(`${share.slug}s${share.isRoot ? 'y' : 'n'}`);

    return shareStrings;
  };
}
